import { Author } from '@/domain'
import { V1ProCoaches } from '@/domain/ProCoaches'
import { authTokenLocalStorageKey } from '@/lib/api/lib/client'
import { AuthenticatedApiClient } from '@/lib/api/authenticatedClient'
import { FetchError } from '@/lib/fetchJson'

const LIST_ALL_PAGE = 500

/**
 * A record that may not exist (most users have no coach record): a 404 is
 * an answer, not a failure. Anything else still rejects.
 */
async function unlessMissing<T>(request: Promise<T>): Promise<T | undefined> {
    try {
        return await request
    } catch (error) {
        if (error instanceof FetchError && error.response.status === 404) {
            return undefined
        }
        throw error
    }
}

export class ProfileClient extends AuthenticatedApiClient {
    async fetchAuthor(authorId?: string): Promise<Author | undefined> {
        const url = `${this.baseUrl}v1CommunityCoaches/${authorId}`
        return unlessMissing(this.fetchWithAuth<Author>(url, { method: 'GET' }))
    }

    async fetchProAttributes(
        authorId?: string
    ): Promise<V1ProCoaches | undefined> {
        const url = `${this.baseUrl}v1ProCoaches/${authorId}`
        return unlessMissing(
            this.fetchWithAuth<V1ProCoaches>(url, { method: 'GET' })
        )
    }

    async listProCoaches(
        page?: number,
        perPage?: number,
        filters?: string
    ): Promise<{ items: V1ProCoaches[]; totalPages: number }> {
        const res = await this.list<V1ProCoaches>('v1ProCoaches', {
            sort: '-created',
            skipTotal: 'false',
            perPage: String(perPage ?? 10),
            page: String(page ?? 1),
            expand: 'coach',
            fields: '*,expand.coach.login,expand.coach.level,expand.coach.bio,expand.coach.id,expand.coach.photo',
            ...(filters ? { filter: filters } : {})
        })
        return { items: res.items, totalPages: res.totalPages ?? 0 }
    }

    /**
     * The newest athletes who joined at or after `since` (unix ms), newest
     * first, and how many in all. Coaches (see listCoachIds) and people who
     * opted out of the leaderboard are left out.
     */
    async listNewcomers(
        since: number,
        limit: number
    ): Promise<{ people: Author[]; total: number }> {
        // PocketBase compares dates as "YYYY-MM-DD HH:MM:SS.sssZ" strings.
        const date = new Date(since).toISOString().replace('T', ' ')
        const res = await this.list<Author>('v1CommunityCoaches', {
            filter: `created>="${date}"&&leaderboardHidden!=true&&v1ProCoaches_via_coach.id=null`,
            sort: '-created',
            fields: 'id,login,photo,level',
            perPage: String(limit)
        })
        return { people: res.items, total: res.totalItems ?? res.items.length }
    }

    /**
     * Ids of every coach: anyone with a public coach profile (a v1ProCoaches
     * record, the list the coaches page and home "Top Coaches" come from),
     * active or not. Levels do not count: "pro" is a paid athlete.
     */
    async listCoachIds(): Promise<Set<string>> {
        const pros = await this.listAll<{ coach: string }>('v1ProCoaches', {
            fields: 'coach'
        })
        return new Set(pros.map((p) => p.coach))
    }

    async fetchAuthors(ids: string[]): Promise<Author[]> {
        if (ids.length === 0) return []
        const res = await this.list<Author>('v1CommunityCoaches', {
            filter: `(${ids.map((id) => `id='${id}'`).join('||')})`,
            skipTotal: 'true',
            perPage: String(ids.length)
        })
        return res.items
    }

    // One page of a collection through the PocketBase proxy.
    private async list<T>(
        collection: string,
        params: Record<string, string>
    ): Promise<{ items: T[]; totalItems?: number; totalPages?: number }> {
        const res = await this.fetchWithAuth<{
            items?: T[]
            totalItems?: number
            totalPages?: number
        }>(`${this.baseUrl}${collection}?${new URLSearchParams(params)}`, {
            method: 'GET'
        })
        return { ...res, items: res.items || [] }
    }

    // Every record of a collection, a page at a time: page one tells how
    // many pages there are, the rest load in parallel.
    private async listAll<T>(
        collection: string,
        params: Record<string, string>
    ): Promise<T[]> {
        const page = (n: number) =>
            this.list<T>(collection, {
                ...params,
                perPage: String(LIST_ALL_PAGE),
                page: String(n)
            })
        const first = await page(1)
        const rest = await Promise.all(
            Array.from({ length: (first.totalPages ?? 1) - 1 }, (_, i) =>
                page(i + 2)
            )
        )
        return [first, ...rest].flatMap((p) => p.items)
    }
}

export const profileClient = new ProfileClient(
    `${import.meta.env.VITE_COLLECTIONS_HOST}/api/v1/collections/`,
    () => localStorage.getItem(authTokenLocalStorageKey) || ''
)
